import { BUID } from '../models/BUID';
import { UserOrganisation } from '../models/organisation-membership';
import { UserProfile } from '../models/user-profile';

/**
 * Central mapping layer for a user's organisation memberships.
 *
 * The `organisations` collection on the profile is the canonical representation
 * of a user's memberships; the top-level `organisationId`, `organisationName`
 * and `buids` are legacy fields kept for backwards compatibility. All reads of
 * organisation/business-unit data should go through these functions so the
 * legacy fallback logic lives in exactly one place.
 *
 * Pure functions (no DI) so guards, services and components can all use them.
 */

/** Synthesises a UserOrganisation from the legacy top-level profile fields. */
function legacyOrganisation(profile: UserProfile): UserOrganisation | null {
  if (!profile?.organisationId) {
    return null;
  }

  return {
    organisationId: profile.organisationId,
    organisationName: profile.organisationName ?? '',
    businessUnits: (profile.buids ?? []).map(b => ({
      buid: b.id,
      name: b.name,
      favourite: b.favourite,
      vorMerchantAccessOptions: b.vorMerchantAccessOptions,
      mids: b.mids,
    })),
  };
}

/**
 * The organisation the user is currently acting in. The legacy top-level
 * `organisationId` remains the active-org pointer (moved server-side via
 * PUT /users/{userId}/organisation/select); this is the one place that reads it
 * to resolve the full organisation.
 */
export function getActiveOrganisation(profile: UserProfile | null | undefined): UserOrganisation | null {
  if (!profile) {
    return null;
  }

  const match = profile.organisations?.find(o => o.organisationId === profile.organisationId);
  return match ?? legacyOrganisation(profile);
}

/** The id of the organisation the user is currently acting in. */
export function getActiveOrganisationId(profile: UserProfile | null | undefined): string {
  return profile?.organisationId ?? '';
}

/** All organisations the user belongs to (falls back to the legacy single org). */
export function getAvailableOrganisations(profile: UserProfile | null | undefined): UserOrganisation[] {
  if (!profile) {
    return [];
  }

  if (profile.organisations && profile.organisations.length > 0) {
    return profile.organisations;
  }

  const legacy = legacyOrganisation(profile);
  return legacy ? [legacy] : [];
}

/**
 * The active organisation's business units in the BUID shape components consume.
 * Prefers the legacy flat `buids` (which carry full mid data and always describe
 * the active organisation) and falls back to the organisations collection.
 */
export function getBusinessUnitsForActiveOrg(profile: UserProfile | null | undefined): BUID[] {
  if (!profile) {
    return [];
  }

  if (profile.buids && profile.buids.length > 0) {
    return profile.buids;
  }

  const active = getActiveOrganisation(profile);
  return (active?.businessUnits ?? []).map(bu => ({
    id: bu.buid,
    name: bu.name,
    favourite: bu.favourite ?? false,
    vorMerchantAccessOptions: bu.vorMerchantAccessOptions ?? [],
    mids: bu.mids ?? [],
  }));
}

/** Whether the user belongs to more than one organisation. */
export function isMultiOrg(profile: UserProfile | null | undefined): boolean {
  return (profile?.organisations?.length ?? 0) > 1;
}
